import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Textarea } from '@/components/ui/Textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { cn } from '@/lib/utils';
import {
  initialsOf,
  type PeoplePickerProfile,
} from '@/components/projects/PeoplePicker';

// Matches an in-progress `@mention` token at the caret: an `@` that
// starts the text or follows whitespace, then a run of non-space chars.
// Names can contain spaces, but the *typed query* deliberately can't —
// a space ends the token (Slack / GitHub style), which is what closes
// the menu naturally once a full name has been inserted.
const TRIGGER_RE = /(^|\s)@(\S*)$/;

function matchesQuery(p: PeoplePickerProfile, query: string): boolean {
  const q = query.toLowerCase();
  if (!q) return true;
  const name = (p.full_name ?? '').toLowerCase();
  const haystack = `${name} ${p.email.toLowerCase()}`;
  return haystack.includes(q) || name.replace(/\s+/g, '').includes(q);
}

/**
 * Resolve which candidate ids are actually mentioned in `text`, given a
 * label→id map built as the user picked people. A mention "counts" only
 * if its exact `@Label` token still appears in the body — so deleting
 * the text removes the mention, and stale picks are dropped. We match on
 * a word boundary after the label so "@Jo" doesn't satisfy "@Jo Ann".
 */
function resolveMentionIds(
  text: string,
  labelToId: Map<string, string>,
): string[] {
  const ids: string[] = [];
  for (const [label, id] of labelToId) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`@${escaped}(?![\\w])`);
    if (re.test(text)) ids.push(id);
  }
  return Array.from(new Set(ids));
}

interface MentionTextareaProps {
  value: string;
  onChange: (next: string) => void;
  /** Called with the current set of mentioned profile ids on every edit. */
  onMentionsChange: (ids: string[]) => void;
  /** People who can be tagged (the project's members). */
  candidates: PeoplePickerProfile[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  /** Forwarded to the underlying textarea (e.g. Ctrl/Cmd+Enter to send). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
}

/**
 * A Textarea with `@mention` autocomplete. Storage stays plain text —
 * mentions are just `@Full Name` in the body — while the resolved
 * profile ids are surfaced via `onMentionsChange` so the caller can fan
 * out notifications. Candidates are the project members, enforcing the
 * "you can only tag people who can access this project" rule in the UI
 * (the DB re-checks it too).
 */
export const MentionTextarea = forwardRef<
  HTMLTextAreaElement,
  MentionTextareaProps
>(function MentionTextarea(
  {
    value,
    onChange,
    onMentionsChange,
    candidates,
    placeholder,
    disabled,
    className,
    autoFocus,
    onKeyDown,
    onBlur,
  },
  forwardedRef,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  // Label→id for everyone the user has picked (persists across edits so
  // resolveMentionIds can re-check the current body).
  const labelToId = useRef<Map<string, string>>(new Map());
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  // Char offset of the `@` that opened the current menu.
  const anchorRef = useRef<number | null>(null);

  const filtered = useMemo(() => {
    if (!menuOpen) return [];
    return candidates.filter((p) => matchesQuery(p, query)).slice(0, 6);
  }, [candidates, query, menuOpen]);

  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(0);
  }, [filtered.length, activeIdx]);

  const emitMentions = useCallback(
    (text: string) => {
      onMentionsChange(resolveMentionIds(text, labelToId.current));
    },
    [onMentionsChange],
  );

  const closeMenu = () => {
    setMenuOpen(false);
    setQuery('');
    anchorRef.current = null;
  };

  const syncMenuFromCaret = (text: string, caret: number) => {
    const upToCaret = text.slice(0, caret);
    const m = upToCaret.match(TRIGGER_RE);
    if (!m) {
      closeMenu();
      return;
    }
    anchorRef.current = caret - m[2].length - 1;
    setQuery(m[2]);
    setMenuOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    onChange(text);
    emitMentions(text);
    syncMenuFromCaret(text, e.target.selectionStart ?? text.length);
  };

  const pick = (p: PeoplePickerProfile) => {
    const el = innerRef.current;
    const anchor = anchorRef.current;
    if (!el || anchor == null) return;
    const label = p.full_name ?? p.email;
    labelToId.current.set(label, p.id);

    const caret = el.selectionStart ?? value.length;
    const before = value.slice(0, anchor);
    const after = value.slice(caret);
    const insert = `@${label} `;
    const next = before + insert + after;
    onChange(next);
    emitMentions(next);
    closeMenu();

    // Restore the caret just after the inserted mention.
    const nextCaret = (before + insert).length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % filtered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(filtered[activeIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div className="relative">
      <Textarea
        ref={setRefs}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={(e) =>
          syncMenuFromCaret(
            (e.target as HTMLTextAreaElement).value,
            (e.target as HTMLTextAreaElement).selectionStart ?? 0,
          )
        }
        onClick={(e) =>
          syncMenuFromCaret(
            (e.target as HTMLTextAreaElement).value,
            (e.target as HTMLTextAreaElement).selectionStart ?? 0,
          )
        }
        onBlur={() => {
          // Delay so a mousedown on a menu row registers before blur
          // tears the menu down.
          window.setTimeout(closeMenu, 120);
          onBlur?.();
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={className}
      />

      {menuOpen && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-72 max-w-full overflow-hidden rounded-md border border-border bg-popover shadow-md">
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.map((p, idx) => (
              <button
                key={p.id}
                type="button"
                // onMouseDown (not onClick) so the pick fires before the
                // textarea's blur handler closes the menu.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(p);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm',
                  idx === activeIdx ? 'bg-secondary/70' : 'hover:bg-secondary/50',
                )}
              >
                <Avatar className="h-6 w-6">
                  {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
                  <AvatarFallback className="text-[9px]">
                    {initialsOf(p)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-foreground">
                    {p.full_name ?? p.email}
                  </div>
                  {p.full_name && (
                    <div className="truncate text-xs text-muted-foreground">
                      {p.email}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
